import { useMutation, useQueryClient } from '@tanstack/react-query'
import { productApi } from 'services'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'
import type { Product } from 'types'

interface UseSyncProductFromShopifyOptions {
  onSuccess?: (product: Product) => void
  onError?: (error: any) => void
}

export const useSyncProductFromShopify = (options?: UseSyncProductFromShopifyOptions) => {
  const queryClient = useQueryClient()
  const { open: notification } = useSnackbar()

  const mutation = useMutation({
    mutationFn: async (productId: string) => {
      return await productApi.syncFromShopify(productId)
    },
    onSuccess: (product) => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: ['product', product.id] })
      queryClient.invalidateQueries({ queryKey: ['products'] })

      notification({
        type: 'success',
        title: 'Product synced successfully',
        text: 'Product data has been updated from Shopify',
      })

      options?.onSuccess?.(product)
    },
    onError: (error: any) => {
      const message = getErrorMessage(error)

      notification({
        type: 'error',
        // VRG-607 — "Sync" stays: it is this product's own user-facing word for
        // the Shopify connection, not jargon borrowed from the code. What changed
        // is the fallback, which was "Failed to sync product from Shopify" — log
        // voice, and it only restated the title in longer form.
        title: 'Sync failed',
        text: message || "We couldn't sync this product from Shopify. Please try again.",
      })

      options?.onError?.(error)
    },
  })

  return {
    syncProduct: mutation.mutate,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    data: mutation.data,
  }
}
